import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { ControlRight } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { dateFormatter } from 'app/utils/customFunctions'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo } from 'react'

export interface Props {
  category?: string
  createdDate?: Date
  image?: ImageProps
  languageCode: string
  paragraph?: string
  title?: string
  URL: string
}

export const Article = memo(function Article({
  category,
  createdDate,
  image,
  languageCode,
  paragraph,
  title,
  URL,
}: Props) {
  if (!URL) {
    return null
  }

  let shortDescription = paragraph?.substring(0, 120)

  return (
    <Container to={URL}>
      <Background>{image ? <Image {...image} /> : null}</Background>

      <Wrapper>
        <Head dial={4} row>
          {category ? <Category>{category}</Category> : null}

          {createdDate ? (
            <Date>{dateFormatter(createdDate, languageCode)}</Date>
          ) : null}
        </Head>

        {title ? <Title>{title}</Title> : null}

        {shortDescription ? (
          <Description
            dangerouslySetInnerHTML={{ __html: shortDescription + '...' }}
          />
        ) : null}

        <ControlRight />
      </Wrapper>
    </Container>
  )
})

const Background = styled.div`
  aspect-ratio: 1.38;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}

    transform: scale(1.1);
    transition: 0.3s ease-in-out;
  }
`

const Wrapper = styled.div`
  margin-top: 2rem;
  transition: 0.3s ease-in-out;

  svg {
    margin-top: 1rem;
    width: 1.5rem;
  }

  @media (max-width: 1199px) {
    margin-top: 1.25rem;
  }
`
const Head = styled(FlexBox)``

const Category = styled.span`
  ${styles.label}

  font-weight: 500;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  padding-right: 1rem;
  position: relative;

  &::after {
    content: '';
    width: 0.0625rem;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    background: ${rgba(theme.colors.variants.neutralDark2, 0.2)};
  }
`

const Date = styled.div`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  text-transform: uppercase;
  padding-left: 1rem;
`

const Title = styled.h3`
  ${styles.title.xs}

  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  margin-top: 0.375rem;

  @media (max-width: 1199px) {
    font-size: 1.5rem;
    line-height: 2.125rem;
  }
`

const Description = styled.p`
  ${styles.description.dark}

  margin-top: 1rem;
`

const Container = styled(Link)`
  display: block;
  text-align: start;

  &:hover {
    ${Background} {
      img {
        transform: scale(1);
      }
    }

    ${Wrapper} {
      transform: translateY(-0.5rem);
    }
  }
`
